// src/components/ScriptPanel.js
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { buildShotlistXlsx } from "../utils/exportShotlistXlsx";
import { buildShotlistHtml } from "../utils/exportShotlistHtml";
import {
  buildNotionZipPackage,
  buildZipPackage,
} from "../utils/exportShotlistZip";
import { downloadBlobFile, downloadTextFile } from "../utils/downloadTextFile";
import { buildStoryboardPdf } from "../utils/exportStoryboardPdf";
import { SUPPORTED_LANGS, useI18n } from "../utils/i18n";
import { getSceneNumberLabel } from "../utils/parseScenes";
import { parseCsv } from "../utils/parseCsv";
import {
  DEFAULT_SHOT_FRAME_FORMAT,
  SHOT_FRAME_FORMATS,
} from "../utils/frameFormats";

const normalizeScriptText = (s) => String(s ?? "").replace(/\r\n?/g, "\n");

function getSelectionOffsetsIn(containerEl) {
  const sel = window.getSelection?.();
  if (!sel || sel.rangeCount === 0) return { start: 0, end: 0 };

  const range = sel.getRangeAt(0);
  if (!containerEl || !containerEl.contains(range.commonAncestorContainer)) {
    return { start: 0, end: 0 };
  }

  const preStart = range.cloneRange();
  preStart.selectNodeContents(containerEl);
  preStart.setEnd(range.startContainer, range.startOffset);
  const start = preStart.toString().length;

  const preEnd = range.cloneRange();
  preEnd.selectNodeContents(containerEl);
  preEnd.setEnd(range.endContainer, range.endOffset);
  const end = preEnd.toString().length;

  return { start, end };
}

export default function ScriptPanel(props = {}) {
  const {
    scriptText,
    setScriptText,
    onScriptLoaded,
    onProjectLoaded,
    onImportShotsFromCsv,
    fileName = "",
    setFileName = () => {},
    scenes,
    selectedSceneId,
    scriptViewMode,
    onToggleView,
    sceneSelection,
    setSceneSelection,
    scriptSelection,
    setScriptSelection,
    shots,
    selectedShotId,
    setSelectedShotId,
    includeLinkedShotsInCounts,
    setIncludeLinkedShotsInCounts,
    linkedLetterReorder,
    setLinkedLetterReorder,
    sceneMoveMode,
    setSceneMoveMode,
    shotReorderMovesText,
    setShotReorderMovesText,
    onApplyScriptEdits,
    onScriptEditDelta,
    shotPrefixMode,
    setShotPrefixMode,
    shotEditorTermsLang,
    setShotEditorTermsLang,
    scriptCollapsed,
    onToggleScriptCollapsed,
    editorInScriptPanel,
    onToggleEditorInScriptPanel,
  } = props;
  const shotFrameFormat = props.shotFrameFormat || DEFAULT_SHOT_FRAME_FORMAT;
  const setShotFrameFormat = props.setShotFrameFormat || (() => {});

  const { lang, setLang, t } = useI18n();

  const fileInputRef = useRef(null);
  const projectInputRef = useRef(null);

  const [isScriptLocked, setIsScriptLocked] = useState(true); // ✅ default = locked

  const scenePreviewRef = useRef(null);

  const scriptAreaRef = useRef(null);

  // shotId -> DOM element du span .shotMark
  const shotMarkRefs = useRef(new Map());

  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // "export" | "import" | null
  const [scriptExportOpen, setScriptExportOpen] = useState(false); // sous-section "Scénario"
  const burgerRef = useRef(null);

  const [syncedScriptText, setSyncedScriptText] = useState("");
  const isScriptDirty = useMemo(() => (scriptText || "") !== (syncedScriptText || ""), [
    scriptText,
    syncedScriptText,
  ]);

    // en haut avec les autres useState
  const [burgerHost, setBurgerHost] = useState(null);

  useEffect(() => {
    if (typeof document === "undefined") return;

    const find = () => document.getElementById("appBurgerSlot");

    // tentative immédiate
    const first = find();
    if (first) {
      setBurgerHost(first);
      return;
    }

    // mini retry (au cas où le header arrive juste après le commit)
    let raf = 0;
    let tries = 0;
    const tick = () => {
      tries += 1;
      const el = find();
      if (el || tries >= 30) setBurgerHost(el); // ~30 frames max
      else raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(raf);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    setOpenSubmenu(null);
    setScriptExportOpen(false);
  }, []);

  const openSettings = () => setSettingsOpen(true);
  const closeSettings = () => setSettingsOpen(false);

  const onMenuAction = (fn) => {
    closeMenu();
    fn?.();
  };

  const openFilePicker = () => fileInputRef.current?.click();
  const openProjectPicker = () => projectInputRef.current?.click();

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const raw = typeof reader.result === "string" ? reader.result : "";
      const text = normalizeScriptText(raw);

      setScriptText(text);
      setFileName(file.name);
      setIsScriptLocked(true);
      onScriptLoaded?.(text);
      setSyncedScriptText(text);
    };
    reader.readAsText(file);
  };

  const onProjectFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = typeof reader.result === "string" ? reader.result : "";
        const project = JSON.parse(raw);

        if (!project || typeof project !== "object") {
          throw new Error("Invalid project object");
        }

        // update local display filename (used for export base names)
        if (typeof project.fileName === "string") setFileName(project.fileName);

        setIsScriptLocked(true); // ✅ relock à l'import projet
        onProjectLoaded?.(project);
      } catch (err) {
        window.alert(t("project.invalid"));
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };

    reader.readAsText(file);

    // allow re-importing same file
    e.target.value = "";
  };

  // -----------------------------
  // Import découpage CSV (mapping colonnes)
  // -----------------------------
  const csvInputRef = useRef(null);
  const [csvModal, setCsvModal] = useState(null);
  // csvModal = { fileName, headers:[], rows:[[]], hasHeader:bool, mapping:{fieldKey:colIndex} }

  const CSV_FIELD_KEYS = [
    "sceneRef",
    "description",
    "shotSize",
    "angle",
    "focus",
    "framing",
    "movement",
    "lensMm",
    "lights",
    "durationEstimateSec",
    "isMaster",
  ];

  const csvFieldLabel = (key) => {
    switch (key) {
      case "sceneRef": return t("csv.field.scene");
      case "description": return t("pdf.field.description");
      case "shotSize": return t("field.shotSize");
      case "angle": return t("field.angle");
      case "focus": return t("field.focus");
      case "framing": return t("field.framing");
      case "movement": return t("field.movement");
      case "lensMm": return t("field.lens");
      case "lights": return t("field.lights");
      case "durationEstimateSec": return t("field.estimation");
      case "isMaster": return t("shotEditor.master");
      default: return key;
    }
  };

  const guessCsvMapping = (headers) => {
    const norm = (s) =>
      String(s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .trim();

    const synonyms = {
      sceneRef: ["scene", "scène", "sequence", "séquence", "seq", "sc"],
      description: ["description", "action", "note", "notes", "texte", "contenu"],
      shotSize: ["echelle", "shot size", "taille", "valeur", "shot value"],
      angle: ["angle"],
      focus: ["focus", "point"],
      framing: ["framing", "cadre", "cadrage"],
      movement: ["movement", "mouvement", "mvt"],
      lensMm: ["lens", "focale", "objectif", "mm"],
      lights: ["light", "lumiere", "lumière", "lights"],
      durationEstimateSec: ["duration", "duree", "durée", "temps", "estimation", "sec"],
      isMaster: ["master"],
    };

    // Les tokens courts (≤ 3 lettres : sc, seq, mm, sec…) exigent une correspondance
    // exacte de l'en-tête, pour éviter les faux positifs ("deSCription" -> "sc").
    const headerMatches = (hn, syn) =>
      syn.length <= 3 ? hn === syn : hn === syn || hn.includes(syn);

    const mapping = {};
    const used = new Set();
    for (const key of CSV_FIELD_KEYS) {
      const list = synonyms[key] || [];
      let found = -1;
      headers.forEach((h, i) => {
        if (found >= 0 || used.has(i)) return;
        const hn = norm(h);
        if (list.some((syn) => headerMatches(hn, syn))) found = i;
      });
      if (found >= 0) used.add(found);
      mapping[key] = found;
    }
    return mapping;
  };

  // ---- Correspondance des scènes (valeur CSV -> scène de l'app) ----
  const csvNormSceneKey = (s) =>
    String(s ?? "")
      .trim()
      .toUpperCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^A-Z0-9]+/g, " ")
      .trim();

  const guessSceneIdForValue = (value) => {
    const key = csvNormSceneKey(value);
    if (!key) return "";
    const list = scenes || [];
    for (let i = 0; i < list.length; i++) {
      const s = list[i];
      if (
        csvNormSceneKey(getSceneNumberLabel(s, i)) === key ||
        csvNormSceneKey(String(i + 1)) === key
      ) {
        return s.id;
      }
    }
    for (const s of list) {
      const tk = csvNormSceneKey(s.title);
      if (tk && (tk === key || tk.includes(key) || key.includes(tk))) return s.id;
    }
    return "";
  };

  const csvSceneColIndex =
    csvModal && csvModal.mapping ? csvModal.mapping.sceneRef : -1;

  // valeurs de scène distinctes rencontrées dans la colonne mappée
  const csvDistinctScenes = useMemo(() => {
    if (!csvModal || csvSceneColIndex == null || csvSceneColIndex < 0) return [];
    const dataRows = csvModal.hasHeader ? csvModal.rows.slice(1) : csvModal.rows;
    const seen = new Set();
    const out = [];
    for (const row of dataRows) {
      const v = String(row[csvSceneColIndex] ?? "").trim();
      if (!seen.has(v)) {
        seen.add(v);
        out.push(v);
      }
    }
    return out;
  }, [csvModal, csvSceneColIndex]);

  // pré-remplit la correspondance (auto-match), en conservant les choix déjà faits
  useEffect(() => {
    if (!csvModal) return;
    setCsvModal((prev) => {
      if (!prev) return prev;
      const assign = { ...(prev.sceneAssign || {}) };
      const include = { ...(prev.sceneInclude || {}) };
      let changed = false;
      for (const val of csvDistinctScenes) {
        if (assign[val] == null) {
          assign[val] = guessSceneIdForValue(val) || (scenes?.[0]?.id ?? "");
          changed = true;
        }
        if (include[val] == null) {
          include[val] = true; // inclus par défaut
          changed = true;
        }
      }
      const nextAll =
        prev.sceneAssignAll == null ? scenes?.[0]?.id ?? "" : prev.sceneAssignAll;
      if (!changed && nextAll === prev.sceneAssignAll) return prev;
      return { ...prev, sceneAssign: assign, sceneInclude: include, sceneAssignAll: nextAll };
    });
  }, [csvDistinctScenes, scenes]);

  const setCsvSceneAssign = (value, sceneId) =>
    setCsvModal((prev) =>
      prev
        ? { ...prev, sceneAssign: { ...(prev.sceneAssign || {}), [value]: sceneId } }
        : prev
    );

  const setCsvSceneInclude = (value, included) =>
    setCsvModal((prev) =>
      prev
        ? { ...prev, sceneInclude: { ...(prev.sceneInclude || {}), [value]: included } }
        : prev
    );

  const setCsvSceneAssignAll = (sceneId) =>
    setCsvModal((prev) => (prev ? { ...prev, sceneAssignAll: sceneId } : prev));

  const openCsvPicker = () => {
    if (csvInputRef.current) {
      csvInputRef.current.value = "";
      csvInputRef.current.click();
    }
  };

  const onCsvFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = typeof reader.result === "string" ? reader.result : "";
      const { rows } = parseCsv(text);
      if (!rows.length) {
        window.alert(t("csv.empty"));
        return;
      }
      const headers = rows[0].map((h, i) => String(h || "").trim() || `Col ${i + 1}`);
      setCsvModal({
        fileName: file.name,
        headers,
        rows,
        hasHeader: true,
        mapping: guessCsvMapping(headers),
      });
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const setCsvMapping = (fieldKey, colIndex) => {
    setCsvModal((prev) =>
      prev ? { ...prev, mapping: { ...prev.mapping, [fieldKey]: colIndex } } : prev
    );
  };

  const csvTruthy = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    if (!s) return false;
    return !["0", "false", "non", "no", "n"].includes(s);
  };

  const doCsvImport = () => {
    if (!csvModal) return;
    const { rows, hasHeader, mapping, sceneAssign, sceneAssignAll, sceneInclude } = csvModal;
    const dataRows = hasHeader ? rows.slice(1) : rows;

    const sceneColMapped = mapping?.sceneRef != null && mapping.sceneRef >= 0;

    const get = (row, key) => {
      const idx = mapping[key];
      if (idx == null || idx < 0) return "";
      return String(row[idx] ?? "").trim();
    };

    const records = dataRows
      // ✅ on ignore les scènes CSV décochées dans la correspondance
      .filter((row) => {
        if (!sceneColMapped) return true;
        const sceneRef = get(row, "sceneRef");
        return sceneInclude?.[sceneRef] !== false;
      })
      .map((row) => {
      const sceneRef = get(row, "sceneRef");
      const sceneId = sceneColMapped
        ? (sceneAssign?.[sceneRef] || "")
        : (sceneAssignAll || "");
      return {
        sceneId,
        sceneRef,
        description: get(row, "description"),
        shotSize: get(row, "shotSize"),
        angle: get(row, "angle"),
        focus: get(row, "focus"),
        framing: get(row, "framing"),
        movement: get(row, "movement"),
        lensMm: get(row, "lensMm"),
        lights: get(row, "lights"),
        durationEstimateSec: get(row, "durationEstimateSec"),
        isMaster: csvTruthy(get(row, "isMaster")),
      };
    });

    onImportShotsFromCsv?.(records);
    setCsvModal(null);
  };

  const canExportShots = Boolean((shots ?? []).length);

  const hasAnyShotImage = useMemo(() => {
    return (shots ?? []).some((sh) => {
      const img = sh?.image;
      const dataUrl = typeof img === "string" ? img : img?.dataUrl || "";
      return Boolean(dataUrl);
    });
  }, [shots]);

  const exportExcelSheetsXlsx = async () => {
    if (!canExportShots) return;

    const base =
      (fileName || "shotlist").replace(/\.[^/.]+$/, "").trim() || "shotlist";
    const { filename, blob } = await buildShotlistXlsx({
      shots,
      scenes,
      scriptText,
      lang,
      shotPrefixMode,
      shotFrameFormat,
    });

    downloadBlobFile(filename, blob);
  };

  const exportNotionZip = async () => {
    if (!canExportShots) return;

    const base =
      (fileName || "shotlist").replace(/\.[^/.]+$/, "").trim() || "shotlist";
    const { filename, blob } = await buildNotionZipPackage({
      shots,
      scenes,
      scriptText,
      lang,
      baseName: base,
      shotPrefixMode,
      shotFrameFormat,
    });

    downloadBlobFile(filename, blob);
  };

  const exportFullPackageZip = async () => {
    if (!canExportShots) return;

    const base =
      (fileName || "shotlist").replace(/\.[^/.]+$/, "").trim() || "shotlist";
    const { filename, blob } = await buildZipPackage({
      shots,
      scenes,
      scriptText,
      lang,
      baseName: base,
      shotPrefixMode,
      shotFrameFormat,
    });

    downloadBlobFile(filename, blob);
  };

  // Export du scénario en l'état (.txt ou .fountain)
  const exportScriptFile = (ext) => {
    if (!scriptText.trim()) return;
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    const base = (fileName || "scenario").replace(/\.[^/.]+$/, "").trim() || "scenario";
    const safeExt = ext === "fountain" ? "fountain" : "txt";
    const filename = `${base}_${y}-${m}-${d}.${safeExt}`;
    downloadTextFile(filename, scriptText, "text/plain;charset=utf-8");
  };

  // ✅ modal update script
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const refreshFileInputRef = useRef(null);

  const closeRefreshModal = () => setRefreshModalOpen(false);

  const confirmRefreshIfNeeded = () => {
    const hasShots = (shots ?? []).length > 0;
    if (!hasShots) return true;
    return window.confirm(t("script.refresh.confirm"));
  };

  const applyFromEditor = () => {
    if (!scriptText.trim()) return;

    // rien à appliquer => on ferme
    if (!isScriptDirty) {
      closeRefreshModal();
      return;
    }

    const baseTextSafe =
      typeof syncedScriptText === "string" && syncedScriptText.length
        ? syncedScriptText
        : scriptText;

    onApplyScriptEdits?.({
      baseText: baseTextSafe,
      nextText: scriptText,
      mode: "editor",
    });

    setSyncedScriptText(scriptText);
    closeRefreshModal();
  };

  const openReimportPicker = () => {
    if (!scriptText.trim()) return;

    if (refreshFileInputRef.current) {
      refreshFileInputRef.current.value = ""; // ✅ permet de re-choisir le même fichier
      refreshFileInputRef.current.click();
    }
  };

  const onRefreshFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const imported = normalizeScriptText(String(reader.result || ""));

      onApplyScriptEdits?.({
        baseText: scriptText,     // ✅ base = texte actuel du projet
        nextText: imported,       // ✅ new = fichier réimporté
        mode: "file",             // ✅ force remap (voir App.js)
        fileName: file.name,
      });

      setSyncedScriptText(imported);
      closeRefreshModal();
    };

    reader.readAsText(file);

    // ✅ reset input
    e.target.value = "";
  };

  // -----------------------------
  // PDF Storyboard modal + config
  // -----------------------------
  const [storyPdfOpen, setStoryPdfOpen] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);

  const [storyPdfCfg, setStoryPdfCfg] = useState({
    projectName: "",
    imagePlacement: "underScript", // "underScript" | "inFrame"
    notesLines: 2,
    alwaysShowEmptyFields: true, // Bug 8 : afficher les sections même vides
    coverEnabled: false, // Bug 11 : page de couverture
    coverText: "",
    creditsEnabled: false, // Bug 11 : page de crédits
    creditsText: "",
    fields: {
      sceneNumber: true,
      shotNumber: true,
      intExt: true,
      location: true,
      timeOfDay: true,

      shotSize: true,
      angle: true,
      focus: true,
      framing: true,
      movement: true,
      focal: true,
      estimation: true,
      lights: true,
      ratio: true,
      colorFormat: true,

      description: true,
      scriptText: true,

      image: true,
      notes: true,
    },
  });

  const STORY_PDF_GROUPS = useMemo(() => {
    const group = (titleKey, keys) => ({
      title: t(titleKey),
      keys,
    });

    return [
      group("pdf.group.1", [
        "sceneNumber",
        "shotNumber",
        "intExt",
        "location",
        "timeOfDay",
      ]),
      group("pdf.group.2", [
        "shotSize",
        "angle",
        "focus",
        "framing",
        "movement",
        "focal",
        "lights",
        "estimation",
        "ratio",
        "colorFormat",
      ]),
      group("pdf.group.3", ["description", "scriptText", "image", "notes"]),
    ];
  }, [t]);

  const openStoryboardPdfConfig = () => {
    const base =
      (fileName || "project").replace(/\.[^/.]+$/, "").trim() || "project";

    setStoryPdfCfg((prev) => {
      const nextFields = { ...(prev.fields || {}) };

      // if no images in any shot => auto uncheck image
      if (!hasAnyShotImage) nextFields.image = false;

      return {
        ...prev,
        projectName: (prev.projectName || "").trim()
          ? prev.projectName
          : base,
        imagePlacement:
          prev.imagePlacement === "inFrame" ? "inFrame" : "underScript",
        notesLines: Math.min(6, Math.max(1, Number(prev.notesLines || 2))),
        fields: nextFields,
      };
    });

    setStoryPdfOpen(true);
  };

  const closeStoryboardPdfConfig = () => setStoryPdfOpen(false);

  const exportStoryboardPdf = async () => {
    if (!canExportShots || pdfExporting) return;

    const fallback =
      (fileName || "project").replace(/\.[^/.]+$/, "").trim() || "project";
    const projectName = (storyPdfCfg.projectName || "").trim() || fallback;

    // ✅ Ferme la config et affiche l'overlay de chargement
    closeStoryboardPdfConfig();
    setPdfProgress(0);
    setPdfExporting(true);

    // laisse React peindre l'overlay avant le travail lourd (bloquant)
    await new Promise((r) => setTimeout(r, 60));

    try {
      const { filename, blob } = await buildStoryboardPdf({
        shots,
        scenes,
        scriptText,
        baseName: projectName,
        shotPrefixMode,
        config: {
          fields: storyPdfCfg.fields,
          imagePlacement: storyPdfCfg.imagePlacement,
          notesLines: storyPdfCfg.notesLines,
          frameFormat: shotFrameFormat || DEFAULT_SHOT_FRAME_FORMAT,
          alwaysShowEmptyFields: storyPdfCfg.alwaysShowEmptyFields,
          cover: storyPdfCfg.coverEnabled
            ? { enabled: true, text: storyPdfCfg.coverText }
            : { enabled: false },
          credits: storyPdfCfg.creditsEnabled
            ? { enabled: true, text: storyPdfCfg.creditsText }
            : { enabled: false },
        },
        lang,
        onProgress: (p) => setPdfProgress(Math.max(0, Math.min(100, Math.round((p || 0) * 100)))),
      });

      setPdfProgress(100);
      downloadBlobFile(filename, blob);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      window.alert(t("pdf.exporting.error"));
    } finally {
      // petit délai pour laisser voir 100 %
      setTimeout(() => setPdfExporting(false), 400);
    }
  };

  // Close burger on outside click / Esc
  useEffect(() => {
    if (!menuOpen) return;

    const onMouseDown = (e) => {
      if (burgerRef.current && !burgerRef.current.contains(e.target)) {
        closeMenu();
      }
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeMenu();
    };

    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen, closeMenu]);

  // Esc closes PDF modal
  useEffect(() => {
    if (!storyPdfOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeStoryboardPdfConfig();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [storyPdfOpen]);

  // Esc closes Settings
  useEffect(() => {
    if (!settingsOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeSettings();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [settingsOpen]);

  // -----------------------------
  // Scene preview + selection sync
  // -----------------------------
  const selectedScene = useMemo(
    () => scenes.find((s) => s.id === selectedSceneId) || null,
    [scenes, selectedSceneId]
  );

  const selectedSceneIndex = useMemo(() => {
    if (!selectedScene) return -1;
    return scenes.findIndex((s) => s.id === selectedScene.id);
  }, [scenes, selectedScene]);

  const displaySceneTitle = useMemo(() => {
    if (!selectedScene) return "";
    const label = getSceneNumberLabel(selectedScene, selectedSceneIndex);
    const baseTitle =
      selectedScene.title?.trim() || t("fallback.scene", { n: label });

    const alreadyPrefixed =
      /^SC\s*\d+[A-Za-z]?\b[.:\-]?/i.test(baseTitle) ||
      /^(SCENE|SCÈNE)\s*\d+[A-Za-z]?\b/i.test(baseTitle);

    return alreadyPrefixed ? baseTitle : `SC${label}. ${baseTitle}`;
  }, [selectedScene, selectedSceneIndex, t]);

  const sceneText = useMemo(() => {
    if (!selectedScene) return "";
    return scriptText.slice(selectedScene.start, selectedScene.end);
  }, [scriptText, selectedScene]);

  const canSceneView = Boolean(scriptText.trim()) && Boolean(selectedScene);

  const switchViewMode = (targetMode) => {
    if (!scriptText.trim()) return;
    if (targetMode === scriptViewMode) return;

    // reset selections when switching view
    setSceneSelection({ start: 0, end: 0 });
    setScriptSelection?.({ start: 0, end: 0 });

    onToggleView?.();
  };

  const shotMarks = useMemo(() => {
    if (!selectedScene) return [];
    const maxLen = sceneText.length;

    const inScene = (shots ?? []).filter((sh) => sh.sceneId === selectedScene.id);

    const mapped = inScene
      .map((sh) => {
        const start = Math.max(0, (sh.scriptRange?.start ?? 0) - selectedScene.start);
        const end = Math.min(maxLen, (sh.scriptRange?.end ?? 0) - selectedScene.start);
        return {
          shotId: sh.id,
          start,
          end,
          color: sh.highlightColor || null,
        };
      })
      .filter((r) => r.end > r.start)
      .sort((a, b) => a.start - b.start);

    const cleaned = [];
    let cursor = 0;
    for (const r of mapped) {
      if (r.end <= cursor) continue;
      const s = Math.max(r.start, cursor);
      cleaned.push({ ...r, start: s });
      cursor = r.end;
    }
    return cleaned;
  }, [shots, selectedScene, sceneText.length]);

  const scenePieces = useMemo(() => {
    const text = sceneText || "";
    if (!text) return [{ type: "text", key: "t0", text: "" }];

    const pieces = [];
    let cursor = 0;

    shotMarks.forEach((m, idx) => {
      if (m.start > cursor) {
        pieces.push({
          type: "text",
          key: `t_${cursor}_${idx}`,
          text: text.slice(cursor, m.start),
        });
      }

      pieces.push({
        type: "mark",
        key: `m_${m.shotId}_${m.start}_${m.end}`,
        shotId: m.shotId,
        text: text.slice(m.start, m.end),
        color: m.color,
      });

      cursor = m.end;
    });

    if (cursor < text.length) {
      pieces.push({
        type: "text",
        key: `t_${cursor}_end`,
        text: text.slice(cursor),
      });
    }

    return pieces;
  }, [sceneText, shotMarks]);

  const syncSceneSelectionFromDOM = () => {
    const el = scenePreviewRef.current;
    if (!el) return;

    const { start, end } = getSelectionOffsetsIn(el);
    setSceneSelection({ start, end });
  };

  const scheduleSyncSelection = () => {
    requestAnimationFrame(syncSceneSelectionFromDOM);
  };

  const editRef = useRef({ start: 0, end: 0 });

  function captureEditRange(ta, inputType) {
    let start = ta.selectionStart ?? 0;
    let end = ta.selectionEnd ?? 0;
    const len = ta.value?.length ?? 0;

    // IMPORTANT: si pas de sélection, Backspace/Delete supprime 1 char
    if (start === end) {
      if (inputType === "deleteContentBackward") {
        // supprime le char avant le curseur
        start = Math.max(0, start - 1);
        end = ta.selectionStart ?? 0;
      } else if (inputType === "deleteContentForward") {
        // supprime le char sous le curseur
        start = ta.selectionStart ?? 0;
        end = Math.min(len, end + 1);
      }
    }

    editRef.current = { start, end };
  }

  useEffect(() => {
  // initialise syncedScriptText uniquement si vide (évite baseText vide au refresh)
  if ((!syncedScriptText || syncedScriptText.length === 0) && scriptText) {
    setSyncedScriptText(scriptText);
  }
  }, [scriptText, syncedScriptText]);

  function scrollTextareaToIndex(ta, index) {
  if (!ta) return;
  const text = ta.value || "";
  const before = text.slice(0, Math.max(0, index));
  const line = before.split("\n").length - 1;

  const cs = window.getComputedStyle(ta);
  const lh = parseFloat(cs.lineHeight) || 16;

  const target = line * lh;
  const nextTop = Math.max(0, target - ta.clientHeight * 0.35);

  ta.scrollTop = nextTop;
  } 

  useEffect(() => {
  if (!selectedShotId) return;

  // ✅ Vue Scène : scroll vers le span .shotMark correspondant
  if (scriptViewMode === "scene") {
    const wrap = scenePreviewRef.current;
    const el = shotMarkRefs.current.get(selectedShotId);
    if (!wrap || !el) return;

    const raf = requestAnimationFrame(() => {
      const wrapRect = wrap.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      const pad = 30;
      const topVisible = wrapRect.top + pad;
      const bottomVisible = wrapRect.bottom - pad;

      const fullyVisible =
        elRect.top >= topVisible && elRect.bottom <= bottomVisible;

      if (fullyVisible) return;

      const delta = elRect.top - topVisible;
      wrap.scrollTo({
        top: Math.max(0, wrap.scrollTop + delta),
        behavior: "auto",
      });
    });

    return () => cancelAnimationFrame(raf);
  }

  // ✅ Vue Full : scroll dans le textarea vers la range du shot
  if (scriptViewMode === "full") {
    const ta = scriptAreaRef.current;
    const sh = (shots || []).find((s) => s.id === selectedShotId);
    if (!ta || !sh?.scriptRange) return;

    // ✅ Ne pas voler le curseur pendant que l'utilisateur écrit dans l'éditeur.
    // (Sinon chaque frappe re-sélectionne la plage du plan -> le curseur "se téléporte".)
    if (document.activeElement === ta) return;

    const raf = requestAnimationFrame(() => {
      const len = ta.value?.length ?? 0;
      const start = Math.max(0, Math.min(sh.scriptRange.start ?? 0, len));
      const end = Math.max(start, Math.min(sh.scriptRange.end ?? start, len));

      try {
        ta.focus({ preventScroll: true });
        ta.setSelectionRange(start, end);
      } catch {}

      scrollTextareaToIndex(ta, start);
    });

    return () => cancelAnimationFrame(raf);
  }
  }, [selectedShotId, scriptViewMode, shots]);
  


  return (
    <div className="panelInner">
      {burgerHost
      ? createPortal(
          <div className="burgerWrap" ref={burgerRef}>
            <button
              className="btn btnGhost burgerBtn"
              type="button"
              aria-haspopup="menu"
              aria-expanded={menuOpen ? "true" : "false"}
              title="Menu"
              onClick={() => setMenuOpen((v) => !v)}
            >
              ☰
            </button>

            {menuOpen && (
              <div className="burgerDropdown" role="menu" aria-label="Menu">
                {/* Import + submenu */}
                <div className="burgerItemWrap">
                  <button
                    className={`burgerItem hasSubmenu ${
                      openSubmenu === "import" ? "open" : ""
                    }`}
                    type="button"
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={openSubmenu === "import" ? "true" : "false"}
                    onClick={() =>
                      setOpenSubmenu((v) => (v === "import" ? null : "import"))
                    }
                  >
                    <span className="burgerItemLeft">
                      <span className="burgerIcon">📥</span>
                      <span className="burgerLabel">{t("menu.import")}</span>
                    </span>
                    <span className="burgerRight">›</span>
                  </button>

                  {openSubmenu === "import" && (
                    <div className="burgerSubmenu" role="menu" aria-label="Import">
                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        onClick={() => onMenuAction(openFilePicker)}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">📄</span>
                          <span className="burgerLabel">
                            {t("menu.import.script")}
                          </span>
                        </span>
                      </button>

                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        onClick={() => onMenuAction(openProjectPicker)}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">🧩</span>
                          <span className="burgerLabel">
                            {t("menu.import.project")}
                          </span>
                        </span>
                      </button>

                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        disabled={!scriptText?.trim()}
                        title={!scriptText?.trim() ? t("menu.import.csv.needScript") : undefined}
                        onClick={() => onMenuAction(openCsvPicker)}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">📑</span>
                          <span className="burgerLabel">
                            {t("menu.import.csv")}
                          </span>
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                {/* Export + submenu */}
                <div className="burgerItemWrap">
                  <button
                    className={`burgerItem hasSubmenu ${
                      openSubmenu === "export" ? "open" : ""
                    }`}
                    type="button"
                    role="menuitem"
                    aria-haspopup="menu"
                    aria-expanded={openSubmenu === "export" ? "true" : "false"}
                    onClick={() =>
                      setOpenSubmenu((v) => (v === "export" ? null : "export"))
                    }
                  >
                    <span className="burgerItemLeft">
                      <span className="burgerIcon">📤</span>
                      <span className="burgerLabel">{t("menu.export")}</span>
                    </span>
                    <span className="burgerRight">›</span>
                  </button>

                  {openSubmenu === "export" && (
                    <div className="burgerSubmenu" role="menu" aria-label="Export">
                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        disabled={!canExportShots}
                        onClick={() => onMenuAction(() => exportExcelSheetsXlsx())}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">📊</span>
                          <span className="burgerLabel">
                            {t("menu.export.excel")}
                          </span>
                        </span>
                      </button>

                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        disabled={!canExportShots}
                        onClick={() => onMenuAction(() => exportNotionZip())}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">🟣</span>
                          <span className="burgerLabel">
                            {t("menu.export.notion")}
                          </span>
                        </span>
                      </button>

                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        disabled={!canExportShots}
                        onClick={() => onMenuAction(() => exportFullPackageZip())}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">📦</span>
                          <span className="burgerLabel">
                            {t("menu.export.full")}
                          </span>
                        </span>
                      </button>

                      <button
                        className="burgerItem"
                        type="button"
                        role="menuitem"
                        disabled={!canExportShots}
                        onClick={() => onMenuAction(openStoryboardPdfConfig)}
                      >
                        <span className="burgerItemLeft">
                          <span className="burgerIcon">🧾</span>
                          <span className="burgerLabel">{t("menu.export.pdf")}</span>
                        </span>
                      </button>

                      <div className="burgerSep" />

                      {/* Sous-section Scénario (repliable) */}
                      <div className="burgerItemWrap">
                        <button
                          className={`burgerItem hasSubmenu ${scriptExportOpen ? "open" : ""}`}
                          type="button"
                          role="menuitem"
                          aria-haspopup="menu"
                          aria-expanded={scriptExportOpen ? "true" : "false"}
                          disabled={!scriptText.trim()}
                          onClick={() => setScriptExportOpen((v) => !v)}
                        >
                          <span className="burgerItemLeft">
                            <span className="burgerIcon">🎬</span>
                            <span className="burgerLabel">{t("menu.export.script")}</span>
                          </span>
                          <span className="burgerRight">›</span>
                        </button>

                        {scriptExportOpen && (
                          <div className="burgerSubmenu" role="menu" aria-label="Scenario">
                            <button
                              className="burgerItem"
                              type="button"
                              role="menuitem"
                              disabled={!scriptText.trim()}
                              onClick={() => onMenuAction(() => exportScriptFile("txt"))}
                            >
                              <span className="burgerItemLeft">
                                <span className="burgerIcon">📄</span>
                                <span className="burgerLabel">{t("menu.export.scriptTxt")}</span>
                              </span>
                            </button>

                            <button
                              className="burgerItem"
                              type="button"
                              role="menuitem"
                              disabled={!scriptText.trim()}
                              onClick={() => onMenuAction(() => exportScriptFile("fountain"))}
                            >
                              <span className="burgerItemLeft">
                                <span className="burgerIcon">🎬</span>
                                <span className="burgerLabel">{t("menu.export.scriptFountain")}</span>
                              </span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="burgerSep" />

                <button
                  className="burgerItem"
                  type="button"
                  role="menuitem"
                  onClick={() => onMenuAction(() => {})}
                >
                  <span className="burgerItemLeft">
                    <span className="burgerIcon">📚</span>
                    <span className="burgerLabel">{t("menu.lexic")}</span>
                  </span>
                </button>

                <button
                  className="burgerItem"
                  type="button"
                  role="menuitem"
                  onClick={() => onMenuAction(() => {})}
                >
                  <span className="burgerItemLeft">
                    <span className="burgerIcon">📖</span>
                    <span className="burgerLabel">{t("menu.wiki")}</span>
                  </span>
                </button>

                <button
                  className="burgerItem"
                  type="button"
                  role="menuitem"
                  onClick={() => onMenuAction(openSettings)}
                >
                  <span className="burgerItemLeft">
                    <span className="burgerIcon">⚙️</span>
                    <span className="burgerLabel">{t("menu.settings")}</span>
                  </span>
                </button>
              </div>
            )}
          </div>,
          burgerHost
        )
      : null}
      
      {scriptCollapsed ? (
        <div className="scriptCollapsedRail">
          <button
            type="button"
            className="btn btnGhost railExpandBtn"
            onClick={() => onToggleScriptCollapsed?.()}
            title={t("script.showPanel")}
            aria-label={t("script.showPanel")}
          >
            ⟩
          </button>
          <div className="railLabel">{t("panel.script.title")}</div>
        </div>
      ) : (
        <>
      <div className="panelHeader">
        <div className="panelHeaderLeft">
          <h2>{t("panel.script.title")}</h2>
        </div>

        <div className="panelActions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".txt,.fountain,text/plain"
            onChange={onFileChange}
            style={{ display: "none" }}
          />

          <input
            ref={projectInputRef}
            type="file"
            accept=".shotlist.json,.json,application/json"
            onChange={onProjectFileChange}
            style={{ display: "none" }}
          />

          <input
            ref={refreshFileInputRef}
            type="file"
            accept=".txt,.fountain,text/plain"
            onChange={onRefreshFileChange}
            style={{ display: "none" }}
          />

          <input
            ref={csvInputRef}
            type="file"
            accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain"
            onChange={onCsvFileChange}
            style={{ display: "none" }}
          />

          {/* ◀ replier le panneau scénario */}
          <button
            type="button"
            className="viewToggleBtn scriptCollapseBtn"
            onClick={() => onToggleScriptCollapsed?.()}
            title={t("script.hidePanel")}
            aria-label={t("script.hidePanel")}
          >
            <span className="viewToggleIcon" aria-hidden="true">⟨</span>
          </button>

          {/* ⇆ afficher le Shot Editor à la place du scénario */}
          <button
            type="button"
            className={`viewToggleBtn scriptSwapEditorBtn ${editorInScriptPanel ? "active" : ""}`}
            onClick={() => onToggleEditorInScriptPanel?.()}
            aria-pressed={editorInScriptPanel ? "true" : "false"}
            title={editorInScriptPanel ? t("script.showScript") : t("script.showEditorHere")}
            aria-label={editorInScriptPanel ? t("script.showScript") : t("script.showEditorHere")}
          >
            <span className="viewToggleIcon" aria-hidden="true">🔧</span>
          </button>

          {scriptViewMode === "full" && (
            <div
              className={`Script tools`}
              role="group"
              aria-label="Script tools"
            >
              {/* 🔒 lock */}
              <button
                type="button"
                className={`viewToggleBtn ${isScriptLocked ? "active" : ""}`}
                onClick={() => setIsScriptLocked((v) => !v)}
                disabled={!scriptText.trim()}
                aria-pressed={isScriptLocked ? "true" : "false"}
                title={isScriptLocked ? "Lock" : "Unlock"}
              >
                <span className="viewToggleIcon" aria-hidden="true">
                  {isScriptLocked ? "🔒" : "🔓"}
                </span>
                <span className="srOnly">{isScriptLocked ? "Lock" : "Unlock"}</span>
              </button>

              {/* 🔄 apply / refresh */}
              <button
                type="button"
                className={`viewToggleBtn ${isScriptDirty ? "dirty" : ""}`}
                disabled={!scriptText.trim()}
                title={t("script.refresh")}
                onClick={() => setRefreshModalOpen(true)}
              >
                <span className="viewToggleIcon" aria-hidden="true">🔄</span>
                <span className="srOnly">{t("script.refresh")}</span>
              </button>
            </div>
          )}

          <div
            className={`viewToggle ${!scriptText.trim() ? "disabled" : ""}`}
            role="group"
            aria-label="View mode"
          >
            <button
              type="button"
              className={`viewToggleBtn ${scriptViewMode === "full" ? "active" : ""}`}
              onClick={() => switchViewMode("full")}
              disabled={!scriptText.trim()}
              aria-pressed={scriptViewMode === "full" ? "true" : "false"}
              title={t("view.script")}
            >
              <span className="viewToggleIcon" aria-hidden="true">
                📄
              </span>
              <span className="srOnly">{t("view.script")}</span>
            </button>

            <button
              type="button"
              className={`viewToggleBtn ${scriptViewMode === "scene" ? "active" : ""}`}
              onClick={() => switchViewMode("scene")}
              disabled={!scriptText.trim()}
              aria-pressed={scriptViewMode === "scene" ? "true" : "false"}
              title={t("view.scene")}
            >
              <span className="viewToggleIcon" aria-hidden="true">
                🎬
              </span>
              <span className="srOnly">{t("view.scene")}</span>
            </button>
          </div>
        </div>
      </div>

      {editorInScriptPanel ? (
        <div id="scriptEditorHost" className="scriptEditorHostPanel" />
      ) : !scriptText ? (
        <div className="emptyState">
          <div className="emptyTitle">{t("script.empty.title")}</div>
          <div className="emptyText">{t("script.empty.text")}</div>
          <button className="btn btnPrimary" onClick={openFilePicker}>
            {t("script.empty.cta")}
          </button>
        </div>
      ) : (
        <>
          {scriptViewMode === "full" && (
            <textarea
              className={`scriptArea ${isScriptLocked ? "isLocked" : ""}`}
              value={scriptText}
              readOnly={isScriptLocked}   // ✅ bloque l’édition, mais laisse la sélection
              ref={scriptAreaRef}

              onSelect={(e) => {
                const ta = e.target;
                setScriptSelection?.({
                  start: ta.selectionStart ?? 0,
                  end: ta.selectionEnd ?? 0,
                });
              }}
              placeholder="Paste your script here…"
              spellCheck={false}
              
              onBeforeInput={(e) => {
                const ta = e.target;
                const inputType = e.nativeEvent?.inputType || e.inputType || "";
                captureEditRange(ta, inputType);
              }}

              onKeyDown={(e) => {
                // fallback (rare) si beforeinput ne couvre pas tout
                if (e.key !== "Backspace" && e.key !== "Delete") return;
                const ta = e.target;
                const inputType = e.key === "Backspace"
                  ? "deleteContentBackward"
                  : "deleteContentForward";
                captureEditRange(ta, inputType);
              }}
              
              onChange={(e) => {
                const next = normalizeScriptText(e.target.value);
                const { start, end } = editRef.current;

                onScriptEditDelta?.({
                  prevText: scriptText,
                  nextText: next,
                  replaceStart: start,
                  replaceEnd: end,
                });

                setScriptText(next);
              }}
            />
          )}

          {scriptViewMode === "scene" && (
            <>
              {!canSceneView ? (
                <div className="emptyState">
                  <div className="emptyTitle">{t("scenePreview.empty.title")}</div>
                  <div className="emptyText">{t("scenePreview.empty.text")}</div>
                </div>
              ) : (
                <>
                  <div className="lineSceneInstruction">
                    <div className="sceneExcerptLabel">
                      <b>{t("scenePreview.title", { title: displaySceneTitle })}</b>
                    </div>
                    <div className="TexteInstructionSelection">
                      {t("scenePreview.instruction")}
                    </div>
                  </div>

                  <div
                    ref={scenePreviewRef}
                    className="scenePreviewRich"
                    tabIndex={0}
                    role="textbox"
                    aria-label={t("scenePreview.title", { title: displaySceneTitle })}
                    onMouseUp={scheduleSyncSelection}
                    onKeyUp={scheduleSyncSelection}
                    onTouchEnd={scheduleSyncSelection}
                  >
                    {scenePieces.map((p) => {
                      if (p.type === "text") return <span key={p.key}>{p.text}</span>;

                      const isActive = p.shotId === selectedShotId;

                      return (
                        <span
                          key={p.key}
                          ref={(el) => {
                            const map = shotMarkRefs.current;
                            if (!el) map.delete(p.shotId);
                            else map.set(p.shotId, el);
                          }}
                          className={`shotMark ${isActive ? "active" : ""}`}
                          style={{ "--mark-color": p.color || "#3749BD" }}
                          title={t("scenePreview.selectShotTip")}
                          onClick={() => setSelectedShotId?.(p.shotId)}
                        >
                          {p.text}
                        </span>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </>
      )}
        </>
      )}

      {/* -----------------------------
          PDF export loading overlay
      ----------------------------- */}
      {pdfExporting && (
        <div className="modalOverlay pdfExportOverlay" role="alertdialog" aria-modal="true">
          <div className="modalCard pdfExportCard">
            <div className="pdfExportSpinner" aria-hidden="true" />
            <h3 className="modalTitle">{t("pdf.exporting.title")}</h3>
            <p className="pdfExportText">{t("pdf.exporting.text")}</p>
            <div className="pdfProgressTrack" aria-hidden="true">
              <div className="pdfProgressBar" style={{ width: `${pdfProgress}%` }} />
            </div>
            <div className="pdfProgressLabel">{pdfProgress}%</div>
          </div>
        </div>
      )}

      {/* -----------------------------
          CSV import mapping modal
      ----------------------------- */}
      {csvModal && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("csv.modal.title")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setCsvModal(null);
          }}
        >
          <div className="modalCard modalW_pdf" onMouseDown={(e) => e.stopPropagation()}>
            <button className="modalCloseButton" type="button" onClick={() => setCsvModal(null)} title="Close">
              ✕
            </button>

            <h3 className="modalTitle">{t("csv.modal.title")}</h3>

            <div className="modalBody">
              <div className="storyPdfHint">{t("csv.modal.help")}</div>

              <div className="csvFileInfo">
                <strong>{csvModal.fileName}</strong>
                {" — "}
                {t("csv.rows", {
                  count: csvModal.hasHeader
                    ? Math.max(0, csvModal.rows.length - 1)
                    : csvModal.rows.length,
                })}
              </div>

              <label className="storyPdfCheck" style={{ marginTop: 8 }}>
                <input
                  type="checkbox"
                  checked={csvModal.hasHeader}
                  onChange={(e) =>
                    setCsvModal((p) => ({
                      ...p,
                      hasHeader: e.target.checked,
                      mapping: e.target.checked ? guessCsvMapping(p.headers) : p.mapping,
                    }))
                  }
                />
                <span className="storyPdfCheckLabel">{t("csv.hasHeader")}</span>
              </label>

              <div className="csvMapGrid">
                {CSV_FIELD_KEYS.map((key) => (
                  <div className="csvMapRow" key={key}>
                    <label className="csvMapLabel">
                      {csvFieldLabel(key)}
                      {key === "sceneRef" ? " *" : ""}
                    </label>
                    <select
                      className="csvMapSelect"
                      value={csvModal.mapping[key] ?? -1}
                      onChange={(e) => setCsvMapping(key, Number(e.target.value))}
                    >
                      <option value={-1}>{t("csv.field.none")}</option>
                      {csvModal.headers.map((h, i) => (
                        <option key={i} value={i}>
                          {csvModal.hasHeader ? h : `Col ${i + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>

              <div className="storyPdfHint" style={{ marginTop: 8 }}>
                {t("csv.field.scene.help")}
              </div>

              {/* Correspondance des scènes */}
              {(() => {
                const sceneOptions = (scenes || []).map((s, i) => (
                  <option key={s.id} value={s.id}>
                    {`SC${getSceneNumberLabel(s, i)} — ${s.title || `Scène ${i + 1}`}`}
                  </option>
                ));

                if (csvSceneColIndex >= 0) {
                  return (
                    <div className="csvSceneMap">
                      <div className="storyPdfSubTitle">{t("csv.scene.mapTitle")}</div>
                      <div className="csvSceneMapList">
                        {csvDistinctScenes.map((val) => {
                          const included = csvModal.sceneInclude?.[val] !== false;
                          return (
                            <div
                              className={`csvSceneMapRow ${included ? "" : "isExcluded"}`}
                              key={val || "__empty__"}
                            >
                              <label
                                className="csvSceneInclude"
                                title={t("csv.scene.include")}
                              >
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={(e) => setCsvSceneInclude(val, e.target.checked)}
                                />
                              </label>
                              <span className="csvSceneValue" title={val}>
                                {val || t("csv.scene.emptyValue")}
                              </span>
                              <span className="csvSceneArrow">→</span>
                              <select
                                className="csvMapSelect"
                                value={csvModal.sceneAssign?.[val] ?? ""}
                                onChange={(e) => setCsvSceneAssign(val, e.target.value)}
                                disabled={!included}
                              >
                                <option value="">{t("csv.scene.choose")}</option>
                                {sceneOptions}
                              </select>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }

                // pas de colonne scène : tout dans une scène choisie
                return (
                  <div className="csvSceneMap">
                    <div className="storyPdfSubTitle">{t("csv.scene.allTitle")}</div>
                    <select
                      className="csvMapSelect"
                      value={csvModal.sceneAssignAll ?? ""}
                      onChange={(e) => setCsvSceneAssignAll(e.target.value)}
                    >
                      <option value="">{t("csv.scene.choose")}</option>
                      {sceneOptions}
                    </select>
                  </div>
                );
              })()}
            </div>

            <div className="storyPdfFooter">
              <button className="btn" type="button" onClick={() => setCsvModal(null)}>
                {t("csv.cancel")}
              </button>
              <button className="btn btnPrimary" type="button" onClick={doCsvImport}>
                {t("csv.import.cta")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -----------------------------
          PDF Config Modal
      ----------------------------- */}
      {storyPdfOpen && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("pdf.modal.title")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeStoryboardPdfConfig();
          }}
        >
          <div className="modalCard modalW_pdf" onMouseDown={(e) => e.stopPropagation()}>
            <button className="modalCloseButton" type="button" onClick={closeStoryboardPdfConfig} title="Close">
              ✕
            </button>

            <h3 className="modalTitle">{t("pdf.modal.title")}</h3>

            <div className="modalBody">
              <div className="storyPdfRow">
                <label className="storyPdfLabel">{t("pdf.projectName")}</label>
                <input
                  className="storyPdfInput"
                  type="text"
                  placeholder={t("pdf.projectName.placeholder")}
                  value={storyPdfCfg.projectName}
                  onChange={(e) =>
                    setStoryPdfCfg((prev) => ({ ...prev, projectName: e.target.value }))
                  }
                />
              </div>

              <div className="storyPdfGroups">
                {STORY_PDF_GROUPS.map((group, gi) => (
                  <div key={group.title} className="storyPdfGroup">
                    <div className="storyPdfGroupHeader">
                      <span className="storyPdfGroupTitle">{group.title}</span>
                    </div>

                    <div className="storyPdfGroupGrid">
                      {group.keys.map((fieldKey) => {
                        const imageDisabled = fieldKey === "image" && !hasAnyShotImage;

                        return (
                          <label key={fieldKey} className="storyPdfCheck">
                            <input
                              type="checkbox"
                              disabled={imageDisabled}
                              checked={!!storyPdfCfg.fields[fieldKey]}
                              onChange={(e) =>
                                setStoryPdfCfg((prev) => ({
                                  ...prev,
                                  fields: { ...prev.fields, [fieldKey]: e.target.checked },
                                }))
                              }
                            />
                            <span className="storyPdfCheckLabel">
                              {t(`pdf.field.${fieldKey}`)}
                            </span>
                          </label>
                        );
                      })}
                    </div>

                    {gi === 2 && (
                      <>
                      {/* Réglages globaux (Bug 8) */}
                      <div className="storyPdfGlobalRow">
                        <div className="storyPdfSubTitle">{t("pdf.global.title")}</div>
                        <label className="storyPdfCheck">
                          <input
                            type="checkbox"
                            checked={!!storyPdfCfg.alwaysShowEmptyFields}
                            onChange={(e) =>
                              setStoryPdfCfg((prev) => ({
                                ...prev,
                                alwaysShowEmptyFields: e.target.checked,
                              }))
                            }
                          />
                          <span className="storyPdfCheckLabel">
                            {t("pdf.global.alwaysShowEmpty")}
                          </span>
                        </label>
                        <div className="storyPdfHint">{t("pdf.global.alwaysShowEmpty.help")}</div>
                      </div>

                      <div className="storyPdfSubRow">
                        {/* COL 1 — IMAGE */}
                        <div className="storyPdfSubBlock storyPdfSubCol">
                          <div className="storyPdfSubTitle">{t("pdf.image.title")}</div>

                          {!hasAnyShotImage ? (
                            <div className="storyPdfHint">{t("pdf.image.none")}</div>
                          ) : storyPdfCfg.fields.image ? (
                            <div className="storyPdfRadios">
                              <label className="storyPdfRadio">
                                <input
                                  type="radio"
                                  name="pdfImagePlacement"
                                  value="underScript"
                                  checked={storyPdfCfg.imagePlacement === "underScript"}
                                  onChange={() =>
                                    setStoryPdfCfg((prev) => ({
                                      ...prev,
                                      imagePlacement: "underScript",
                                    }))
                                  }
                                />
                                <span>{t("pdf.image.underScript")}</span>
                              </label>

                              <label className="storyPdfRadio">
                                <input
                                  type="radio"
                                  name="pdfImagePlacement"
                                  value="inFrame"
                                  checked={storyPdfCfg.imagePlacement === "inFrame"}
                                  onChange={() =>
                                    setStoryPdfCfg((prev) => ({
                                      ...prev,
                                      imagePlacement: "inFrame",
                                    }))
                                  }
                                />
                                <span>{t("pdf.image.inFrame")}</span>
                              </label>
                            </div>
                          ) : (
                            <div className="storyPdfHint">{t("pdf.image.helpEnable")}</div>
                          )}
                        </div>

                        {/* COL 2 — NOTES */}
                        <div className="storyPdfSubBlock storyPdfSubCol">
                          <div className="storyPdfSubTitle">{t("pdf.notes.title")}</div>

                          {storyPdfCfg.fields.notes ? (
                            <div className="storyPdfRowInline">
                              <span className="storyPdfInlineLabel">
                                {t("pdf.notes.lines")}
                              </span>
                              <select
                                className="storyPdfNotesSelect"
                                value={storyPdfCfg.notesLines}
                                onChange={(e) =>
                                  setStoryPdfCfg((prev) => ({
                                    ...prev,
                                    notesLines: Math.min(
                                      6,
                                      Math.max(1, Number(e.target.value || 2))
                                    ),
                                  }))
                                }
                              >
                                {[1, 2, 3, 4, 5, 6].map((n) => (
                                  <option key={n} value={n}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <div className="storyPdfHint">{t("pdf.notes.helpEnable")}</div>
                          )}
                        </div>
                      </div>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Couverture + Crédits (Bug 11) */}
              <div className="storyPdfGroup storyPdfPagesGroup">
                <div className="storyPdfGroupHeader">
                  <span className="storyPdfGroupTitle">{t("pdf.pages.title")}</span>
                </div>

                <div className="storyPdfPageBlock">
                  <label className="storyPdfCheck">
                    <input
                      type="checkbox"
                      checked={!!storyPdfCfg.coverEnabled}
                      onChange={(e) =>
                        setStoryPdfCfg((prev) => ({ ...prev, coverEnabled: e.target.checked }))
                      }
                    />
                    <span className="storyPdfCheckLabel">{t("pdf.cover.enable")}</span>
                  </label>
                  {storyPdfCfg.coverEnabled && (
                    <textarea
                      className="storyPdfTextarea"
                      rows={3}
                      placeholder={t("pdf.cover.placeholder")}
                      value={storyPdfCfg.coverText}
                      onChange={(e) =>
                        setStoryPdfCfg((prev) => ({ ...prev, coverText: e.target.value }))
                      }
                    />
                  )}
                </div>

                <div className="storyPdfPageBlock">
                  <label className="storyPdfCheck">
                    <input
                      type="checkbox"
                      checked={!!storyPdfCfg.creditsEnabled}
                      onChange={(e) =>
                        setStoryPdfCfg((prev) => ({ ...prev, creditsEnabled: e.target.checked }))
                      }
                    />
                    <span className="storyPdfCheckLabel">{t("pdf.credits.enable")}</span>
                  </label>
                  {storyPdfCfg.creditsEnabled && (
                    <textarea
                      className="storyPdfTextarea"
                      rows={4}
                      placeholder={t("pdf.credits.placeholder")}
                      value={storyPdfCfg.creditsText}
                      onChange={(e) =>
                        setStoryPdfCfg((prev) => ({ ...prev, creditsText: e.target.value }))
                      }
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="storyPdfFooter">
              <button className="btn" type="button" onClick={exportStoryboardPdf}>
                {t("pdf.download")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -----------------------------
          Settings Modal
      ----------------------------- */}
      {settingsOpen && (
        <div
          className="modalOverlay"
          role="dialog"
          aria-modal="true"
          aria-label={t("settings.title")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeSettings();
          }}
        >
          <div className="modalCard modalW_settings" onMouseDown={(e) => e.stopPropagation()}>
            <button
              className="modalCloseButton"
              type="button"
              aria-label="Close"
              onClick={closeSettings}
              title="Close"
            >
              ✕
            </button>

            <h3 className="modalTitle">{t("settings.title")}</h3>

            {/* ✅ TOUT le contenu dedans => tout scroll */}
            <div className="modalBody">
              <div className="storyPdfRow">
                <label className="storyPdfLabel">{t("settings.language")}</label>
                <select
                  className="selectLanguage"
                  value={lang}
                  onChange={(e) => setLang(e.target.value)}
                >
                  {SUPPORTED_LANGS.map((l) => (
                    <option key={l} value={l}>
                      {t(`settings.lang.${l}`)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="storyPdfHint" style={{ marginTop: 8 }}>
                {t("settings.language.help")}
              </div>

              <div className="storyPdfRow" style={{ marginTop: 14 }}>
                <label className="storyPdfLabel">{t("settings.termsLang")}</label>
                <select
                  className="selectLanguage"
                  value={shotEditorTermsLang || "auto"}
                  onChange={(e) => setShotEditorTermsLang?.(e.target.value)}
                >
                  <option value="auto">{t("settings.termsLang.auto")}</option>
                  <option value="fr">{t("settings.termsLang.fr")}</option>
                  <option value="en">{t("settings.termsLang.en")}</option>
                </select>
              </div>

              <h4 className="modalAltTitle">{t("settings.alt-title")}</h4>

              <div className="settingsRow">
                {/* Préfixe des plans */}
                <div className="settingsRowSelectPrefix">
                  <label className="settingLabel">{t("settings.shotPrefix")}</label>

                  <select
                    className="settingSelect"
                    value={String(shotPrefixMode || "none")}
                    onChange={(e) => setShotPrefixMode?.(e.target.value)}
                  >
                    <option value="P">{t("settings.shotPrefix.p")}</option>
                    <option value="SH">{t("settings.shotPrefix.sh")}</option>
                    <option value="none">{t("settings.shotPrefix.none")}</option>
                  </select>
                </div>
                <div className="settingsHelp">{t("settings.shotPrefix.help")}</div>

                {/* Format des cadres */}
                <div className="settingsRowSelectPrefix">
                  <label className="settingLabel">{t("settings.frameFormat")}</label>

                  <select
                    className="settingSelect"
                    value={shotFrameFormat || "16_9"}
                    onChange={(e) => {
                      const next = e.target.value;
                      setShotFrameFormat?.(next);
                    }}
                  >
                    {SHOT_FRAME_FORMATS.map((fmt) => (
                      <option key={fmt.value} value={fmt.value}>
                        {fmt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="settingsHelp">{t("settings.frameFormat.help")}</div>

                {/* Compter les plans liés */}
                <label className="settingsCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={!!includeLinkedShotsInCounts}
                    onChange={(e) => setIncludeLinkedShotsInCounts?.(e.target.checked)}
                  />
                  <span>{t("settings.countLinkedShots")}</span>
                </label>
                <div className="settingsHelp">{t("settings.countLinkedShots.help")}</div>

                {/* Renuméroter les lettres des plans liés selon l'ordre */}
                <label className="settingsCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={!!linkedLetterReorder}
                    onChange={(e) => setLinkedLetterReorder?.(e.target.checked)}
                  />
                  <span>{t("settings.linkedLetterReorder")}</span>
                </label>
                <div className="settingsHelp">{t("settings.linkedLetterReorder.help")}</div>

                {/* Mode de déplacement des scènes (mode organisation) */}
                <div className="settingsRow">
                  <div className="settingsRowSelectPrefix">
                    <label className="settingLabel">{t("settings.sceneMoveMode")}</label>
                    <select
                      className="selectLanguage"
                      value={sceneMoveMode === "visual" ? "visual" : "text"}
                      onChange={(e) => setSceneMoveMode?.(e.target.value)}
                    >
                      <option value="text">{t("settings.sceneMoveMode.text")}</option>
                      <option value="visual">{t("settings.sceneMoveMode.visual")}</option>
                    </select>
                  </div>
                  <div className="settingsHelp">{t("settings.sceneMoveMode.help")}</div>
                </div>

                {/* Réordonner aussi le texte quand on renumérote des plans */}
                <label className="settingsCheckboxLabel">
                  <input
                    type="checkbox"
                    checked={!!shotReorderMovesText}
                    onChange={(e) => setShotReorderMovesText?.(e.target.checked)}
                  />
                  <span>{t("settings.shotReorderMovesText")}</span>
                </label>
                <div className="settingsHelp">{t("settings.shotReorderMovesText.help")}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {refreshModalOpen && (
        <div className="modalOverlay" role="dialog" aria-modal="true">
          <div className="modalCard scriptRefreshModal">
            <div className="modalHeaderRow">
              <div className="modalTitle">{t("script.refresh.modalTitle")}</div>
              <button className="modalCloseButton" type="button" onClick={closeRefreshModal}>
                ✕
              </button>
            </div>

            <div className="modalBody">
              <div className="scriptRefreshOptions">
                <div className="scriptRefreshOption">
                  <div className="scriptRefreshOptionIcon" aria-hidden="true">📝</div>
                  <div className="scriptRefreshOptionText">
                    <div className="scriptRefreshOptionTitle">
                      {t("script.refresh.fromEditor")}
                    </div>
                    <div className="scriptRefreshOptionDesc">
                      {t("script.refresh.fromEditor.desc")}
                    </div>
                  </div>
                  <button
                    className="btn btnPrimary"
                    type="button"
                    disabled={!isScriptDirty}
                    onClick={applyFromEditor}
                    title={!isScriptDirty ? t("script.refresh.noChanges") : ""}
                  >
                    {t("script.refresh.fromEditor.cta")}
                  </button>
                </div>

                <div className="scriptRefreshOption">
                  <div className="scriptRefreshOptionIcon" aria-hidden="true">📄</div>
                  <div className="scriptRefreshOptionText">
                    <div className="scriptRefreshOptionTitle">
                      {t("script.refresh.fromFile")}
                    </div>
                    <div className="scriptRefreshOptionDesc">
                      {t("script.refresh.fromFile.desc")}
                    </div>
                  </div>
                  <button
                    className="btn btnPrimary"
                    type="button"
                    onClick={openReimportPicker}
                  >
                    {t("script.refresh.fromFile.cta")}
                  </button>
                </div>
              </div>
            </div>

            <div className="modalFooter">
              <button className="btn" type="button" onClick={closeRefreshModal}>
                {t("script.refresh.cancel")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
